import * as THREE from 'three';
import type { CharacterState } from './CharacterStateMachine';
import type { SlimeMesh } from './Slime';

const GRAB_DURATION = 0.5;

interface Pose {
  squash: number;
  stretch: number;
  wobbleAmp: number;
  wobbleFreq: number;
  lean: number;
  rise: number;
}

const IDLE: Pose = { squash: 1, stretch: 1, wobbleAmp: 0.05, wobbleFreq: 2.4, lean: 0, rise: 0 };
const WALK: Pose = { squash: 1.06, stretch: 0.9, wobbleAmp: 0.14, wobbleFreq: 4.5, lean: 0.0, rise: 0 };
const RUN: Pose = { squash: 1.08, stretch: 1.16, wobbleAmp: 0.2, wobbleFreq: 6.5, lean: 0.06, rise: 0 };
const JUMP: Pose = { squash: 0.9, stretch: 1.45, wobbleAmp: 0.05, wobbleFreq: 3, lean: -0.05, rise: 0.16 };
const FALL: Pose = { squash: 0.95, stretch: 1.28, wobbleAmp: 0.1, wobbleFreq: 4, lean: 0, rise: 0 };
const LAND: Pose = { squash: 0.9, stretch: 1.2, wobbleAmp: 0.12, wobbleFreq: 5, lean: 0, rise: -0.02 };

function poseFor(state: CharacterState): Pose {
  switch (state) {
    case 'Walk': return WALK;
    case 'Run': return RUN;
    case 'Jump': return JUMP;
    case 'Fall': return FALL;
    case 'Land': return LAND;
    default: return IDLE;
  }
}

export class SlimeAnimator {
  private mesh: SlimeMesh;
  private current: CharacterState | null = null;
  private phase = 0;
  private grabTimer = 0;
  private tiltX = 0;
  private tiltZ = 0;
  private motX = 0;
  private motZ = 0;
  private grounded = true;
  private sTiltX = 0;
  private sTiltZ = 0;
  private sBack = 0;
  private sGround = 1;
  private sFx = 0;
  private sFz = 0;
  private impact = 0;
  private impactTarget = 0;
  private reachNX = 0;
  private reachNY = 1;
  private reachNZ = 0;
  private reachTarget = 0;
  private reachCur = 0;
  private reachVel = 0;
  /** Berkedip: jam periodik + durasi animasi tutup-buka. */
  private blinkClock = 0;
  private blinkPeriod = 2.8;
  private blinkAnim = 0;
  private readonly BLINK_DUR = 0.14;
  /** Lirik: offset pandang mata (hanya saat Idle). */
  private lookX = 0;
  private lookY = 0;
  private lookTX = 0;
  private lookTY = 0;
  private lookTimer = 0;

  static readonly INCLINE_THRESHOLD = 0.14;

  private pose: Pose = { ...IDLE };

  constructor(mesh: SlimeMesh) {
    this.mesh = mesh;
  }

  setGroundTilt(tiltX: number, tiltZ: number): void {
    this.tiltX = tiltX;
    this.tiltZ = tiltZ;
  }

  setMotion(vx: number, vz: number): void {
    this.motX = vx;
    this.motZ = vz;
  }

  setGrounded(grounded: boolean): void {
    this.grounded = grounded;
  }

  setImpact(speed: number): void {
    this.impactTarget = Math.min(speed / 26, 0.32);
  }

  setReach(x: number, y: number, z: number, amount: number): void {
    const len = Math.hypot(x, y, z);
    if (len < 1e-4 || amount <= 0) {
      this.reachTarget = 0;
      return;
    }
    this.reachNX = x / len;
    this.reachNY = y / len;
    this.reachNZ = z / len;
    this.reachTarget = amount;
  }

  play(state: CharacterState, _speed = 0): void {
    if (this.current === state) return;
    this.current = state;

    if (state === 'Grab') {
      this.grabTimer = GRAB_DURATION;
    }
  }

  update(delta: number): void {
    if (!this.current) return;

    const state = this.current;
    this.grabTimer = Math.max(0, this.grabTimer - delta);

    const goal = this.computePose(state);
    const rate = state === 'Grab' || state === 'Land' ? 22 : 14;
    this.pose = this.springTowards(this.pose, goal, rate, delta);

    this.phase += delta * this.pose.wobbleFreq;
    const wob2 = Math.sin(this.phase * 2.3 + 0.8);
    const wobX = Math.cos(this.phase * 0.7);

    const { body, innerBall, eyes, group, baseRadius, baseHeight } = this.mesh;

    const ow = this.pose.wobbleAmp;
    const wig = ow * (1 + 0.45 * wob2);

    const smoothF = 1 - Math.exp(-10 * delta);
    this.sTiltX += (this.tiltX - this.sTiltX) * smoothF;
    this.sTiltZ += (this.tiltZ - this.sTiltZ) * smoothF;
    // Nempel cepat (landing tetap responsif), lepas perlahan (takeoff melandai).
    const groundTarget = this.grounded ? 1 : 0;
    const groundRate = groundTarget > this.sGround ? 12 : 4.5;
    this.sGround += (groundTarget - this.sGround) * (1 - Math.exp(-groundRate * delta));

    const tiltMag = Math.hypot(this.sTiltX, this.sTiltZ);
    const nx = tiltMag > 1e-4 ? -this.sTiltX / tiltMag : 0;
    const nz = tiltMag > 1e-4 ? -this.sTiltZ / tiltMag : 0;
    const rawMag = Math.hypot(this.tiltX, this.tiltZ);
    const rawNx = rawMag > 1e-4 ? -this.tiltX / rawMag : 0;
    const rawNz = rawMag > 1e-4 ? -this.tiltZ / rawMag : 0;
    // Bobot kontinu pengganti boolean isOnFloor/isOnInclined: tidak ada
    // flip dalam satu frame saat lepas atau mendarat.
    const tiltSmooth = THREE.MathUtils.smoothstep(
      tiltMag,
      SlimeAnimator.INCLINE_THRESHOLD * 0.5,
      SlimeAnimator.INCLINE_THRESHOLD * 1.5,
    );

    const speed = Math.hypot(this.motX, this.motZ);
    const fx = speed > 0.05 ? this.motX / speed : 0;
    const fz = speed > 0.05 ? this.motZ / speed : 0;
    const speed01 = Math.min(speed / 7, 1);
    const isMoving = state === 'Walk' || state === 'Run';
    const backTarget = isMoving ? speed01 * (state === 'Run' ? 0.85 : 0.5) : 0;
    this.sBack += (backTarget - this.sBack) * smoothF;
    const dirF = 1 - Math.exp(-14 * delta);
    this.sFx += (fx - this.sFx) * dirF;
    this.sFz += (fz - this.sFz) * dirF;
    const backAmt = this.sBack;

    const squash = this.pose.squash;
    const iRate = this.impactTarget > this.impact ? 25 : 8;
    this.impact += (this.impactTarget - this.impact) * (1 - Math.exp(-iRate * delta));
    // Spring karet underdamped: melesat overshoot lalu bergetar mereda.
    this.reachVel += (140 * (this.reachTarget - this.reachCur) - 9 * this.reachVel) * delta;
    this.reachCur += this.reachVel * delta;
    if (this.reachCur < -0.6) {
      this.reachCur = -0.6;
      this.reachVel = 0;
    } else if (this.reachCur > 2) {
      this.reachCur = 2;
      this.reachVel = 0;
    }
    const groundW = Math.max(0, Math.min(1, this.sGround));
    const inclineW = groundW * tiltSmooth;
    const impact = groundW * this.impact;

    const effSquash = squash * (1 - impact);
    const sy = effSquash + wob2 * ow * 0.4;
    const latBase = 1 / Math.sqrt(Math.max(effSquash, 0.4));
    const sLat = latBase * (1 + impact * 0.6) * (1 + wobX * wig);

    body.scale.set(1, 1, 1);
    body.rotation.set(0, 0, 0);

    const posAttr = this.mesh.bodyGeo.attributes.position;
    const arr = posAttr.array as Float32Array;
    const base = this.mesh.bodyBase;
    const hh = this.mesh.baseHeight;
    const r = this.mesh.baseRadius;
    const g = tiltMag;
    const capFrac = 0.55;
    const widthTop = 0.95;
    const widthBottom = 1.6;
    const extAmt = Math.min(g * 1.2, 0.9);
    const backExtAmt = backAmt > 0 ? (state === 'Run' ? 0.7 : 0.25) * backAmt : 0;

    for (let i = 0; i < base.length; i += 3) {
      const x = base[i];
      const y = base[i + 1];
      const z = base[i + 2];
      const hf = y / hh;

      const wf = Math.pow(Math.max(0, 1 - hf), 0.9);
      const wMod = widthTop + (widthBottom - widthTop) * wf;

      const bx = x * wMod * sLat;
      const bz = z * wMod * sLat;
      const baseSlope = bx * nx + bz * nz;
      const sFwd = bx * this.sFx + bz * this.sFz;
      const sBack = -sFwd;

      let rx = bx;
      let rz = bz;

      if (inclineW > 0.001) {
        const u = Math.max(0, Math.min(1, baseSlope / r));
        const downN = u * u * (3 - 2 * u);
        const ext = extAmt * inclineW * downN * (1 - hf);
        rx += nx * ext;
        rz += nz * ext;
      }
      if (backExtAmt > 0 && inclineW < 0.999) {
        const ub = Math.max(0, Math.min(1, sBack / r));
        const bN = ub * ub * (3 - 2 * ub);
        const be = backExtAmt * groundW * (1 - tiltSmooth) * bN * (1 - hf);
        rx -= this.sFx * be;
        rz -= this.sFz * be;
      }

      const groundRef = -rawMag * (rx * rawNx + rz * rawNz) * inclineW;

      let ny = y;
      if (hf < capFrac) {
        const t = hf / capFrac;
        const s = t * t * (3 - 2 * t);
        ny = groundRef + (y - groundRef) * (1 - groundW + groundW * s);
      }

      const frontI = Math.max(-1, Math.min(1, -baseSlope / r));
      const frontB = Math.max(-1, Math.min(1, sFwd / r));
      const front = frontI * tiltSmooth + frontB * (1 - tiltSmooth);
      const amp = Math.min(tiltMag, 1) * tiltSmooth + backAmt * 0.4 * (1 - tiltSmooth);
      const ampW = amp * groundW;
      if (ampW > 0.001) {
        const uFront = 0.5 * (front + 1);
        const env = 1 - ampW * Math.pow(1 - uFront, 2);
        ny = groundRef + (ny - groundRef) * env;
      }

      ny = groundRef + (ny - groundRef) * sy;

      if (backAmt > 0 && sBack > 0) {
        const ak = Math.min(sBack / r, 1) * (1 - hf);
        ny = groundRef + (ny - groundRef) * (1 - ak);
      }

      if (this.reachCur > 0.001) {
        const top = hf * hf * (3 - 2 * hf);
        const rl = this.reachCur * 0.9;
        rx += this.reachNX * rl * top;
        rz += this.reachNZ * rl * top;
        ny += this.reachNY * rl * top;
      }

      arr[i] = rx;
      arr[i + 1] = ny;
      arr[i + 2] = rz;
    }
    posAttr.needsUpdate = true;

    body.position.set(0, 0, 0);

    innerBall.position.set(0, baseHeight * 0.35 * effSquash, 0);
    innerBall.scale.set(sLat, effSquash + wob2 * ow * 0.25, sLat);

    group.rotation.x = this.pose.lean + this.reachNZ * this.reachCur * 0.35;
    group.rotation.z = wobX * ow * 0.15 - this.reachNX * this.reachCur * 0.35;

    group.position.y = this.pose.rise;

    // Berkedip di semua state: tutup-buka cepat tiap 2-4 detik.
    this.blinkClock += delta;
    if (this.blinkClock >= this.blinkPeriod) {
      this.blinkClock = 0;
      this.blinkPeriod = 2.2 + Math.random() * 2.0;
      this.blinkAnim = this.BLINK_DUR;
    }
    let eyeSY = 1;
    if (this.blinkAnim > 0) {
      this.blinkAnim = Math.max(0, this.blinkAnim - delta);
      const t = 1 - this.blinkAnim / this.BLINK_DUR;
      eyeSY = 1 - 0.92 * Math.sin(Math.PI * t);
    }
    // Lirik (gerak mata) hanya saat Idle; state lain kedip saja.
    if (state === 'Idle') {
      this.lookTimer += delta;
      if (this.lookTimer > 1.4) {
        this.lookTimer = 0;
        this.lookTX = (Math.random() * 2 - 1) * 0.09;
        this.lookTY = (Math.random() * 2 - 1) * 0.05;
      }
    } else {
      this.lookTX = 0;
      this.lookTY = 0;
    }
    const lookF = 1 - Math.exp(-6 * delta);
    this.lookX += (this.lookTX - this.lookX) * lookF;
    this.lookY += (this.lookTY - this.lookY) * lookF;

    // Basis posisi mata persis versi asli + offset lirik di atasnya.
    eyes.position.set(
      this.reachNX * this.reachCur * 0.15 + this.lookX,
      baseHeight * 0.62 * effSquash + this.reachNY * this.reachCur * 0.1 + this.lookY,
      this.reachNZ * this.reachCur * 0.15,
    );
    for (const eye of eyes.children) {
      eye.scale.set(1, eyeSY, 1);
    }
    eyes.rotation.x = 0;
    eyes.rotation.z = 0;
    void baseRadius;
    void baseHeight;
  }

  private computePose(state: CharacterState): Pose {
    if (state === 'Grab') {
      const t = 1 - this.grabTimer / GRAB_DURATION;
      const s = Math.sin(t * Math.PI);
      return {
        squash: 1 - 0.18 * s + 0.1 * Math.sin(t * t * Math.PI * 4) * (1 - t),
        stretch: 1 + 0.4 * s,
        wobbleAmp: 0.12 * s,
        wobbleFreq: 0,
        lean: 0.24 * s,
        rise: 0.03 * s,
      };
    }
    return poseFor(state);
  }

  private springTowards(current: Pose, goal: Pose, rate: number, delta: number): Pose {
    const f = 1 - Math.exp(-rate * delta);
    return {
      squash: current.squash + (goal.squash - current.squash) * f,
      stretch: current.stretch + (goal.stretch - current.stretch) * f,
      wobbleAmp: current.wobbleAmp + (goal.wobbleAmp - current.wobbleAmp) * f,
      wobbleFreq: goal.wobbleFreq,
      lean: current.lean + (goal.lean - current.lean) * f,
      rise: current.rise + (goal.rise - current.rise) * f,
    };
  }
}